import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import {
  Alert,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fade,
  FormHelperText,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Ref,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link as RouterLink } from "react-router-dom";

import ChatEventsModal from "@/common/components/Entity/ChatEvent/ChatEventsModal";
import ChatParticipantsModal from "@/common/components/Entity/ChatParticipant/ChatParticipantsModal";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useChatContext } from "@/common/contexts/chat";
import { ChatPermissions } from "@/common/helpers/entity/chat";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper, ValidationInfo } from "@/common/validation";
import {
  AppPermission,
  ChatAcknowledgeResultDto,
  ChatDto,
  ChatParticipantDto,
  ChatParticipantJoinResultDto,
  ChatParticipantLeaveResultDto,
  ChatParticipantStatus,
  ChatType,
  TagEntityType,
} from "@/core/api/generated/v1";
import * as chatParticipantsSlice from "@/store/communication/chatParticipantsSlice";
import * as chatsSlice from "@/store/communication/chatsSlice";
import { ResponsiveStyleValueCustom } from "@/theme/types";

import AppVisibilitySensor from "../../AppVisibilitySensor";
import AuthorizedElement from "../../Auth/AuthorizedElement";
import AppButton from "../../Button/AppButton";
import AppIconButton from "../../Button/AppIconButton";
import { RealtimeChatConnectionStatusIndicator } from "../../Connection/RealtimeChatConnectionStatusIndicator";
import ChatFileUploader from "../../Files/ChatFileUploader";
import AppIcon from "../../Icons/AppIcon";
import ChatTypeIcon from "../../Icons/ChatTypeIcon";
import ConfirmationModal from "../../Modals/ConfirmationModal";
import ChatActivitiesModal from "../ChatActivity/ChatActivitiesModal";
import ChatHistory, { ChatHistoryProps } from "../ChatHistory/ChatHistory";
import ChatMessageSender, { ChatMessageSenderHandle } from "../ChatMessage/ChatMessageSender";
import { ChatMessageInputFeaturesProps } from "../ChatMessage/Input/ChatMessageInput";
import ChatParticipantAvatar from "../ChatParticipant/ChatParticipantAvatar";
import UpdateChatUserSettingsModal from "../ChatUserSettings/UpdateChatUserSettingsModal";
import GeneralAttachedTagsDisplay from "../General/GeneralTag/GeneralAttachedTagsDisplay";
import GeneralAttachedTagsOfEntityEditModal from "../General/GeneralTag/GeneralAttachedTagsOfEntityEditModal";
import ChatInteractionTracker from "./ChatInteractionTracker";
import ChatNegotiationsModal from "./ChatNegotiation/ChatNegotiationsModal";
import ChatPinnedNegotiations from "./ChatNegotiation/ChatPinnedNegotiations";
import ChatPinnedItems from "./ChatPinnedItems";
import UpdateChatModal from "./UpdateChatModal";

const defaultDisplayProps = {
  breadcrumbs: true,
  contentHeader: false,
  actions: true,
  contentFolding: false,
};

export type ChatDisplayProps = Partial<typeof defaultDisplayProps>;

export type ChatSize = "small" | "medium";

export type ChatDisplayHandle = {
  chat: {
    message: {
      input: ChatMessageSenderHandle | null;
    };
  };
};

interface OwnProps {
  allowPin?: boolean;
  allowClose?: boolean;
  allowLeave?: boolean;
  allowAttachments?: boolean;
  allowOpenInNewTab?: boolean;
  allowEdit?: boolean;
  allowParticipantManagement?: boolean;
  allowAcknowledgement?: boolean;
  allowUpdateTags?: boolean;
  size?: ChatSize;
  disabled?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  maxHeight?: string | number;
  chatHistoryHeight?: ResponsiveStyleValueCustom<number>;
  chatHistoryProps?: ChatHistoryProps;
  chatInputFeatures?: ChatMessageInputFeaturesProps;
  displayProps?: ChatDisplayProps;
  onPin?: (chat: ChatDto) => void;
  onClose?: (chat: ChatDto) => void;
  onSendMessage?: () => void;
}

interface Props extends OwnProps {
  chatPerms: ChatPermissions;
  chat: ChatDto;
  participants: ChatParticipantDto[] | undefined;
  hasPinnedItems: boolean | undefined;
  resolveChat: (...arg: Parameters<typeof chatsSlice.resolveChat>) => Promise<ChatDto>;
  reopenChat: (...arg: Parameters<typeof chatsSlice.reopenChat>) => Promise<ChatDto>;
  acknowledgeChat: (
    ...arg: Parameters<typeof chatsSlice.acknowledgeChat>
  ) => Promise<ChatAcknowledgeResultDto>;
  joinChat: (
    ...arg: Parameters<typeof chatParticipantsSlice.joinChat>
  ) => Promise<ChatParticipantJoinResultDto>;
  leaveChat: (
    ...arg: Parameters<typeof chatParticipantsSlice.leaveChat>
  ) => Promise<ChatParticipantLeaveResultDto>;
}

export default forwardRef(function ChatDisplay(
  {
    chatPerms,
    chat,
    participants,
    hasPinnedItems,
    resolveChat,
    reopenChat,
    acknowledgeChat,
    joinChat,
    leaveChat,
    allowPin,
    allowClose,
    allowLeave,
    allowAttachments = true,
    allowOpenInNewTab = true,
    allowEdit = true,
    allowParticipantManagement = true,
    allowAcknowledgement = true,
    allowUpdateTags = true,
    size = "medium",
    fullWidth,
    fullHeight,
    maxHeight,
    chatHistoryHeight,
    chatHistoryProps,
    chatInputFeatures,
    displayProps = defaultDisplayProps,
    disabled,
    onPin,
    onClose,
    onSendMessage,
  }: Props,
  ref: Ref<ChatDisplayHandle>,
) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const theme = useTheme();
  const { enqueueSnackbar } = useAppSnackbar();
  const chatContext = useChatContext();

  const [chatValidation, setChatValidation] = useState<ValidationInfo>(new ValidationInfo());
  const [isResolveLoading, setIsResolveLoading] = useState(false);

  const topAnchorRef = useRef<HTMLElement | null>(null);
  const bottomAnchorRef = useRef(null);
  const chatStartAnchorRef = useRef<HTMLElement | null>(null);
  const chatEndAnchorRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<ChatMessageSenderHandle>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isChatContentFolded, setIsChatContentFolded] = useState(false);
  const [isChatStartAnchorVisible, setIsChatStartAnchorVisible] = useState(false);
  const [isChatEndAnchorVisible, setIsChatEndAnchorVisible] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isChatEventsModalOpen, setIsChatEventsModalOpen] = useState(false);
  const [isChatActivitiesModalOpen, setIsChatActivitiesModalOpen] = useState(false);
  const [isViewParticipantsModalOpen, setIsViewParticipantsModalOpen] = useState(false);
  const [isManageParticipantsModalOpen, setIsManageParticipantsModalOpen] = useState(false);
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);
  const [isViewNegotiationsModalOpen, setIsViewNegotiationsModalOpen] = useState(false);
  const [isLeaveChatConfirmationModalOpen, setIsLeaveChatConfirmationModalOpen] = useState(false);
  const [isChatUserSettingsModalOpen, setIsChatUserSettingsModalOpen] = useState(false);
  const isMenuOpen = Boolean(menuAnchorEl);

  const maxHeightValue = maxHeight || undefined;

  const activeParticipants = useMemo(
    () => participants?.filter((x) => x.status === ChatParticipantStatus.Active),
    [participants],
  );

  useImperativeHandle(
    ref,
    () => ({
      chat: {
        message: {
          input: inputRef.current,
        },
      },
    }),
    [inputRef.current],
  );

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleUpdate = useCallback(() => {
    setIsUpdateModalOpen(true);
    handleMenuClose();
  }, []);

  const handleViewParticipants = useCallback(() => {
    setIsViewParticipantsModalOpen(true);
    handleMenuClose();
  }, []);

  const handleManageParticipants = useCallback(() => {
    setIsManageParticipantsModalOpen(true);
    handleMenuClose();
  }, []);

  const handleResolve = useCallback(async () => {
    try {
      setIsResolveLoading(true);
      await resolveChat({ nexusOpsTenant: EMPTY_TENANT_IDENTIFIER, chatId: chat.id! });
      setChatValidation(new ValidationInfo());
      onClose && onClose(chat);
    } catch (err) {
      setChatValidation(ValidationHelper.handleApiErrorResponse(err));
    } finally {
      setIsResolveLoading(false);
      handleMenuClose();
    }
  }, [chat, resolveChat]);

  const handleReopen = useCallback(async () => {
    try {
      setIsResolveLoading(true);
      await reopenChat({ nexusOpsTenant: EMPTY_TENANT_IDENTIFIER, chatId: chat.id! });
      setChatValidation(new ValidationInfo());
    } catch (err) {
      setChatValidation(ValidationHelper.handleApiErrorResponse(err));
    } finally {
      setIsResolveLoading(false);
      handleMenuClose();
    }
  }, [chat, resolveChat]);

  const handleAcknowledge = useCallback(async () => {
    try {
      await acknowledgeChat({ nexusOpsTenant: EMPTY_TENANT_IDENTIFIER, chatId: chat.id! });
      setChatValidation(new ValidationInfo());
    } catch (err) {
      setChatValidation(ValidationHelper.handleApiErrorResponse(err));
    } finally {
      handleMenuClose();
    }
  }, [chat, acknowledgeChat]);

  const handlePin = () => {
    onPin && onPin(chat);
    handleMenuClose();
  };

  const handleViewEventsHistory = () => {
    setIsChatEventsModalOpen(true);
    handleMenuClose();
  };

  const handleViewActivities = () => {
    setIsChatActivitiesModalOpen(true);
    handleMenuClose();
  };

  const handleClose = () => {
    onClose && onClose(chat);
    handleMenuClose();
  };

  const handleJoin = useCallback(async () => {
    try {
      await joinChat({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        chatId: chat.id!,
      });
      enqueueSnackbar("You've joined the chat.", { variant: "success" });
    } catch (err) {
      const newValidation = ValidationHelper.handleApiErrorResponse(err);
      setChatValidation(newValidation);
      if (newValidation.generalError) {
        enqueueSnackbar(newValidation.generalError, { variant: "error" });
      }
    }
  }, []);

  const handleLeave = useCallback(async () => {
    setIsLeaveChatConfirmationModalOpen(true);
  }, []);

  const handleLeaveConfirmed = useCallback(async () => {
    try {
      await leaveChat({
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        chatId: chat.id!,
      });
      enqueueSnackbar("You've left the chat.", { variant: "success" });
      setIsLeaveChatConfirmationModalOpen(false);

      if (chatContext.chatPerms?.shouldCloseChatIfParticipantLeft) {
        onClose && onClose(chat);
      }
    } catch (err) {
      const newValidation = ValidationHelper.handleApiErrorResponse(err);
      setChatValidation(newValidation);
      if (newValidation.generalError) {
        enqueueSnackbar(newValidation.generalError, { variant: "error" });
      }
    } finally {
      handleMenuClose();
    }
  }, [chat, acknowledgeChat]);

  const handleViewNegotiations = () => {
    setIsViewNegotiationsModalOpen(true);
    handleMenuClose();
  };

  return (
    <Box
      data-testid='chat-display'
      sx={{ height: "inherit", overflow: "hidden", borderRadius: "inherit" }}
    >
      <ChatInteractionTracker chatId={chat.id!}>
        <Card
          sx={{
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            height: "inherit",
            maxHeight: maxHeightValue,
            borderRadius: "inherit",
          }}
        >
          <Box data-testid='chat-header-container'>
            <AppVisibilitySensor
              onChange={(isVisible) => {
                if (isVisible !== isChatStartAnchorVisible) {
                  setIsChatStartAnchorVisible(isVisible);
                }
              }}
            >
              {(params) => <Box {...params.observableProps} ref={chatStartAnchorRef}></Box>}
            </AppVisibilitySensor>

            <CardHeader
              sx={{ py: 1, px: 2 }}
              action={
                <Stack direction='row' sx={{ gap: 0, alignItems: "center" }}>
                  {/* Participants preview */}
                  {activeParticipants && activeParticipants.length > 1 && (
                    <Tooltip title='View all chat participants'>
                      <AvatarGroup
                        sx={{ mr: 1, cursor: "pointer" }}
                        total={activeParticipants.length}
                        max={5}
                        variant={theme.components?.MuiAvatar?.defaultProps?.variant}
                        spacing='medium'
                        slotProps={{
                          surplus: {
                            sx: { width: 20, height: 20, fontSize: "small" },
                          },
                        }}
                        onClick={handleViewParticipants}
                      >
                        {activeParticipants.map((participant, pIndex) => (
                          <ChatParticipantAvatar
                            key={pIndex}
                            chatId={participant.chatId}
                            participant={participant}
                            withOnlineStatus={false}
                            withPopover={false}
                            sx={{ width: 20, height: 20 }}
                          />
                        ))}
                      </AvatarGroup>
                    </Tooltip>
                  )}

                  {/* Resolve/Reopen */}
                  {chat.settings!.allowResolution && (
                    <>
                      {chatPerms.canResolveChat && !chat.isResolved && (
                        <Button
                          variant='text'
                          color='text'
                          loading={isResolveLoading}
                          onClick={handleResolve}
                        >
                          Resolve
                        </Button>
                      )}

                      {chatPerms.canReopenChat && chat.isResolved && (
                        <Button
                          variant='text'
                          color='text'
                          loading={isResolveLoading}
                          onClick={handleReopen}
                        >
                          Re-open
                        </Button>
                      )}
                    </>
                  )}

                  {/* Chat actions */}
                  {displayProps.actions && chatContext.chatPerms?.canViewChatActions && (
                    <>
                      <AppIconButton onClick={(e) => setMenuAnchorEl(e.currentTarget)}>
                        <AppIcon of='moreVert' />
                      </AppIconButton>

                      <Menu anchorEl={menuAnchorEl} open={isMenuOpen} onClose={handleMenuClose}>
                        <ListItem sx={{ pt: 0 }}>
                          <ListItemText
                            sx={{ m: 0, p: 0 }}
                            primary={
                              <Typography
                                component='div'
                                variant='body2'
                                color='text.secondary'
                                sx={{ m: 0, p: 0 }}
                              >
                                Chat
                              </Typography>
                            }
                          />
                        </ListItem>
                        {allowOpenInNewTab && (
                          <MenuItem
                            dense
                            component={RouterLink}
                            to={ROUTE_PATH.CHAT_VIEW(chat.id, { chatType: chat.type })}
                            target='_blank'
                          >
                            <ListItemIcon>
                              <AppIcon of='openInNew' fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>Open in new tab</ListItemText>
                          </MenuItem>
                        )}
                        {allowEdit && chatContext.chatPerms?.canUpdateChat && (
                          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                            <MenuItem dense onClick={handleUpdate}>
                              <ListItemIcon>
                                <AppIcon of='edit' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Edit</ListItemText>
                            </MenuItem>
                          </AuthorizedElement>
                        )}
                        {allowUpdateTags && chatContext.chatPerms?.canUpdateChatTags && (
                          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                            <MenuItem
                              dense
                              onClick={() => {
                                setIsEditTagsModalOpen(true);
                                handleMenuClose();
                              }}
                            >
                              <ListItemIcon>
                                <AppIcon of='tag' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Edit tags</ListItemText>
                            </MenuItem>
                          </AuthorizedElement>
                        )}
                        {!chat.settings!.allowParticipantManagement &&
                          chatContext.chatPerms?.canViewParticipants && (
                            <MenuItem dense onClick={handleViewParticipants}>
                              <ListItemIcon>
                                <GroupIcon fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Participants</ListItemText>
                            </MenuItem>
                          )}
                        {allowParticipantManagement &&
                          chat.settings!.allowParticipantManagement &&
                          chatContext.chatPerms?.canManageParticipants && (
                            <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                              <MenuItem dense onClick={handleManageParticipants}>
                                <ListItemIcon>
                                  <GroupAddIcon fontSize='small' />
                                </ListItemIcon>
                                <ListItemText>Participants</ListItemText>
                              </MenuItem>
                            </AuthorizedElement>
                          )}
                        {allowAcknowledgement &&
                          chat.settings!.allowAcknowledgement &&
                          chatContext.chatPerms?.canAcknowledgeChat && (
                            <MenuItem dense onClick={handleAcknowledge}>
                              <ListItemIcon>
                                <AppIcon of='acknowledge' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Acknowledge</ListItemText>
                            </MenuItem>
                          )}
                        {/* Pin chat on UI */}
                        {allowPin && (
                          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                            <MenuItem dense onClick={handlePin}>
                              <ListItemIcon>
                                <AppIcon of='pin' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Pin</ListItemText>
                            </MenuItem>
                          </AuthorizedElement>
                        )}
                        {chatContext.chatPerms?.canViewEventHistory && (
                          <MenuItem dense onClick={handleViewEventsHistory}>
                            <ListItemIcon>
                              <AppIcon of='history' fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>Event history</ListItemText>
                          </MenuItem>
                        )}
                        {chatContext.chatPerms?.canViewActivities && (
                          <MenuItem dense onClick={handleViewActivities}>
                            <ListItemIcon>
                              <AppIcon of='history' fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>Chat activities</ListItemText>
                          </MenuItem>
                        )}
                        {chatContext.chatPerms?.canViewUserSettings && (
                          <MenuItem dense onClick={() => setIsChatUserSettingsModalOpen(true)}>
                            <ListItemIcon>
                              <AppIcon of='settings' fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>My chat settings</ListItemText>
                          </MenuItem>
                        )}

                        {/* Close chat on UI */}
                        {allowClose && (
                          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                            <MenuItem dense onClick={handleClose}>
                              <ListItemIcon>
                                <AppIcon of='close' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>Close</ListItemText>
                            </MenuItem>
                          </AuthorizedElement>
                        )}

                        {chatContext.chatPerms?.canViewNegotiations && (
                          <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                            <Divider />
                            <ListItem sx={{ pt: 0 }}>
                              <ListItemText
                                sx={{ m: 0, p: 0 }}
                                primary={
                                  <Typography
                                    component='div'
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ m: 0, p: 0 }}
                                  >
                                    Negotiations
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <MenuItem dense onClick={handleViewNegotiations}>
                              <ListItemIcon>
                                <AppIcon of='negotiation' fontSize='small' />
                              </ListItemIcon>
                              <ListItemText>View negotiations</ListItemText>
                            </MenuItem>
                          </AuthorizedElement>
                        )}

                        {allowLeave && chatContext.chatPerms?.canLeaveChat && <Divider />}
                        {allowLeave && chatContext.chatPerms?.canLeaveChat && (
                          <MenuItem dense onClick={handleLeave}>
                            <ListItemIcon>
                              <AppIcon of='leave' fontSize='small' color='error' />
                            </ListItemIcon>
                            <ListItemText sx={{ color: (theme2) => theme2.palette.error.main }}>
                              Leave
                            </ListItemText>
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                </Stack>
              }
              title={
                <Typography component='div' variant='subtitle1'>
                  <Box component='span'>{chat.name}</Box>{" "}
                  <Typography
                    component='span'
                    variant='body2'
                    color='text.secondary'
                    sx={{ ml: 0.5 }}
                  >
                    <ChatTypeIcon chatType={chat.type} fontSize='small' withTooltip inText />
                  </Typography>
                </Typography>
              }
              subheader={
                <Typography component='div' variant='body2' color='text.secondary'>
                  {chat.description && <Box>{chat.description}</Box>}

                  {/* Tags */}
                  {chat.tags && (
                    <AuthorizedElement permissions={[AppPermission.FleetAppAccess]}>
                      <GeneralAttachedTagsDisplay tags={chat.tags} defaultIsFolded />
                    </AuthorizedElement>
                  )}
                </Typography>
              }
            />

            {chatValidation.generalError && (
              <FormHelperText error={!!chatValidation.generalError} sx={{ py: 1, px: 2 }}>
                {chatValidation.generalError}
              </FormHelperText>
            )}
            <Divider />

            {/* Pinned items */}
            {hasPinnedItems && (
              <>
                <CardContent sx={{ py: 0.5 }}>
                  <ChatPinnedItems chat={chat} participants={participants} />
                </CardContent>
                <Divider />
              </>
            )}

            {/* Pinned negotiations */}
            {chat.type === ChatType.Negotiation && chat.settings!.isPinNegotiations && (
              <>
                <CardContent sx={{ py: 1 }}>
                  <ChatPinnedNegotiations chat={chat} />
                </CardContent>
                <Divider />
              </>
            )}
          </Box>

          {/* Chat main content */}
          <Box
            data-testid='chat-content-container'
            sx={{ /*height: "fill-available"*/ height: "100%", overflow: "hidden" }}
          >
            <ChatFileUploader chatId={chat.id!} disabled={!allowAttachments || disabled}>
              {({
                uploadedFiles,
                renderUploadButton,
                renderUploadList,
                handlePastedFiles,
                getUploadedFiles,
                clearUploads,
              }) => (
                <>
                  {/* Content header */}
                  {displayProps?.contentHeader && (
                    <CardContent sx={{ flex: 1 }}>
                      <Stack direction='row' spacing={2}>
                        <Box>
                          {displayProps?.contentFolding && (
                            <AppIconButton
                              sx={{ p: 0 }}
                              onClick={() => setIsChatContentFolded(!isChatContentFolded)}
                            >
                              <AppIcon
                                of={isChatContentFolded ? "expandMore" : "expandLess"}
                                fontSize='small'
                              />
                            </AppIconButton>
                          )}{" "}
                          <Typography component='span' variant='subtitle1'>
                            Chat
                          </Typography>
                        </Box>

                        <Stack
                          direction='row'
                          spacing={1}
                          sx={{ flex: 1, justifyContent: "flex-end" }}
                        >
                          {chatContext.chatPerms?.canViewUserSettings && (
                            <Tooltip title='My chat settings'>
                              <AppIconButton
                                size='small'
                                onClick={() => setIsChatUserSettingsModalOpen(true)}
                              >
                                <AppIcon of='settings' />
                              </AppIconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>

                      <Fade in={!isChatContentFolded} unmountOnExit>
                        <Divider sx={{ mt: 1 }} />
                      </Fade>
                      <Box></Box>
                    </CardContent>
                  )}

                  <Fade in={!isChatContentFolded} unmountOnExit>
                    <Box sx={{ height: "inherit", display: "flex", flexDirection: "column" }}>
                      {/* Chat history */}
                      <CardContent
                        data-testid='chat-history'
                        sx={{
                          display: "flex",
                          flex: 1,
                          overflow: "hidden",
                          width: "100%",
                          px: { xxs: 1, md: 2 },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            // maxWidth: fullWidth ? "100%" : "400px",
                            maxHeight: fullHeight ? "100%" : (chatHistoryHeight ?? 400),
                            display: "flex",
                          }}
                        >
                          <Box ref={topAnchorRef}></Box>

                          <ChatHistory
                            chat={chat}
                            participants={participants}
                            {...chatHistoryProps}
                          />

                          <Box ref={bottomAnchorRef}></Box>
                        </Box>
                      </CardContent>

                      <RealtimeChatConnectionStatusIndicator />

                      {/* Message sender */}
                      {/* <Divider /> */}
                      {chatContext.chatPerms?.canSendMessage && (
                        <Box
                          data-testid='chat-message-sender-container'
                          sx={{
                            py: { xxs: 1, md: 1.5 },
                            px: { xxs: 1, md: 2 },
                            backgroundColor: "transparent",
                          }}
                        >
                          <ChatMessageSender
                            ref={inputRef}
                            disabled={disabled}
                            chat={chat}
                            uploadedAttachments={uploadedFiles}
                            size={size}
                            secondaryActions={allowAttachments ? [renderUploadButton()] : []}
                            features={chatInputFeatures}
                            renderAttachments={() => renderUploadList()}
                            onPasteFiles={handlePastedFiles}
                            getUploadedAttachments={getUploadedFiles}
                            clearUploadedAttachments={clearUploads}
                            onSendMessage={onSendMessage}
                          />
                        </Box>
                      )}

                      {/* Join chat action */}
                      {chatContext.chatPerms?.canJoinChat && (
                        <Box>
                          <Alert severity='info'>
                            {`This chat is read-only for now. Want to stay updated or start
                          replying?`}{" "}
                            <AppButton
                              variant='contained'
                              color='primary'
                              size='extraSmall'
                              isAutoTrackLoading
                              onClick={handleJoin}
                            >
                              Join chat
                            </AppButton>
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  </Fade>
                </>
              )}
            </ChatFileUploader>

            <AppVisibilitySensor
              onChange={(isVisible) => {
                if (isVisible !== isChatEndAnchorVisible) {
                  setIsChatEndAnchorVisible(isVisible);
                }
              }}
            >
              {(params) => <Box {...params.observableProps} ref={chatEndAnchorRef}></Box>}
            </AppVisibilitySensor>
          </Box>
        </Card>
      </ChatInteractionTracker>

      {/* Scroll to chat button */}
      {!isChatEndAnchorVisible && (
        <AppButton
          variant='contained'
          color='text'
          startIcon={<AppIcon of='arrowDownward' />}
          sx={{
            position: "fixed",
            bottom: "5%",
            left: "50%",
            transform: "translateX(-50%)",
            opacity: 0.8,
            boxShadow: (th) => th.boxShadowCustom?.action[2],
          }}
          onClick={() => chatContext.requestScrollToMessageInputAndFocus()}
        >
          Scroll to the chat
        </AppButton>
      )}

      {/* Update chat modal */}
      <UpdateChatModal
        open={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
        onUpdated={() => setIsUpdateModalOpen(false)}
        chat={chat}
      />

      {/* Chat event history modal */}
      {chat && (
        <ChatEventsModal
          open={isChatEventsModalOpen}
          onClose={() => setIsChatEventsModalOpen(false)}
          chatId={chat.id!}
        />
      )}

      {/* Chat activities modal */}
      {chat && (
        <ChatActivitiesModal
          open={isChatActivitiesModalOpen}
          onClose={() => setIsChatActivitiesModalOpen(false)}
          chatId={chat.id!}
        />
      )}

      {/* View participants  modal */}
      <ChatParticipantsModal
        open={isViewParticipantsModalOpen}
        onClose={() => setIsViewParticipantsModalOpen(false)}
        onUpdated={() => setIsViewParticipantsModalOpen(false)}
        chat={chat}
        allowManage={false}
      />

      {/* Manage participants modal */}
      <ChatParticipantsModal
        open={isManageParticipantsModalOpen}
        onClose={() => setIsManageParticipantsModalOpen(false)}
        onUpdated={() => setIsManageParticipantsModalOpen(false)}
        chat={chat}
        allowManage
      />

      {/* Edit tags modal */}
      <GeneralAttachedTagsOfEntityEditModal
        open={isEditTagsModalOpen}
        onClose={() => setIsEditTagsModalOpen(false)}
        entityType={TagEntityType.Chat}
        entityId={chat.id!}
      />

      {/* Update chat user settings modal */}
      {isChatUserSettingsModalOpen && (
        <UpdateChatUserSettingsModal
          open={isChatUserSettingsModalOpen}
          onClose={() => setIsChatUserSettingsModalOpen(false)}
          updateChatUserSettingsProps={{
            chatId: chat.id!,
            onSave: () => setIsChatUserSettingsModalOpen(false),
          }}
        />
      )}

      {/* Negotiations modal */}
      {chat && (
        <ChatNegotiationsModal
          chatId={chat.id!}
          open={isViewNegotiationsModalOpen}
          onClose={() => setIsViewNegotiationsModalOpen(false)}
        />
      )}

      {/* Chat leave confirmation modal */}
      <ConfirmationModal
        title='Leave the chat?'
        body={
          <>
            {`You're going to leave the chat `}
            {chat.name ? (
              <Typography component='span' fontWeight='bold'>
                {chat.name}
              </Typography>
            ) : (
              ""
            )}
            . <Typography component='span'>Please confirm the action.</Typography>
          </>
        }
        open={isLeaveChatConfirmationModalOpen}
        onClose={() => setIsLeaveChatConfirmationModalOpen(false)}
        onCancel={() => setIsLeaveChatConfirmationModalOpen(false)}
        onConfirm={handleLeaveConfirmed}
      />
    </Box>
  );
});
